/* eslint-disable @typescript-eslint/no-shadow */
import React, {
  memo,
  useMemo,
} from 'react';

import {
  Button,
  message,
  Tooltip,
  Typography,
} from 'antd';
import TMIcon from 'tm-icons-library';

import { UploadOutlined } from '@ant-design/icons';

import { useTheme } from '../../../../context/ThemeProvider';
import { MEDIA_CONTENT } from '../../../../shared/constants';
import {
  AcceptedFileType,
  getFileExtension,
  uploadFiles,
  uploadFilesHandler,
} from '../../../../shared/file';
import {
  downloadFileWithProgress,
  getSpecificFileIcon,
} from '../../../../shared/fileUtils';
import { numFormatterInUnit } from '../../../../shared/formatter';
import { AllowedFileTypeInQuestion } from '../../../../types/assignmentTypes';
import { IFileAttachment } from '../../../../types/fileTypes';
import { ACCEPT_FILE_TYPES } from '../../constants';
import AttachmentItem from './AttachmentItem';

interface Props {
  mode: 'add' | 'view';
  attachmentList?: IFileAttachment[];
  onAttachmentListChange?: (attachmentList: IFileAttachment[]) => void;
  attachments: File[];
  setAttachments: React.Dispatch<React.SetStateAction<File[]>>;
  maxFileCount: number;
  maxFileSize: number; // in bytes
  allowedFileTypes: AllowedFileTypeInQuestion[];
  renderAttachmentItem?: (
    attachment: IFileAttachment,
    options: {
      isFile?: boolean;
      onDownload?: (attachment: IFileAttachment) => void;
      onRemove?: (attachment: IFileAttachment) => void;
    } & React.HTMLAttributes<HTMLDivElement>,
  ) => React.ReactNode;
}

const AddAttachments: React.FC<Props> = ({
  mode = 'add',
  attachmentList,
  onAttachmentListChange,
  attachments,
  setAttachments,
  maxFileCount,
  maxFileSize, // in bytes
  allowedFileTypes,
  renderAttachmentItem,
}) => {
  const { colors: COLORS } = useTheme();

  const mediaItems = useMemo(() => {
    return [...(attachmentList || []), ...attachments].map((file) => {
      if (file instanceof File) {
        return {
          isFile: true,
          name: file.name,
          size: file.size,
          type: file.type,
          url: URL.createObjectURL(file),
          key: file.name,
        };
      }

      return file;
    });
  }, [attachments, attachmentList]);

  const uploadAttachments = (files: File[]) => {
    setAttachments((prev) => [...prev, ...files]);
  };

  if (mode === 'view' && attachmentList?.length) {
    return (
      <div className="add-or-view-attachments">
        <div className="attachments__list">
          {attachmentList.map((attachment) => {
            if (renderAttachmentItem) {
              return renderAttachmentItem(attachment, {
                onDownload: () =>
                  downloadFileWithProgress(attachment.url, attachment.name),
              });
            }

            return (
              <AttachmentItem
                key={attachment.url}
                attachment={attachment}
                onDownload={({ url, name }) =>
                  downloadFileWithProgress(url, name)
                }
              />
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="add-or-view-attachments">
      <div className="attachments__list">
        {mediaItems.map((item) => {
          const { url, key, name, size, isFile } = item as IFileAttachment & {
            isFile: boolean;
          };

          const fileType = name ? getFileExtension(name, 'doc') : '';

          if (renderAttachmentItem) {
            return renderAttachmentItem(item as IFileAttachment, {
              isFile,
              onDownload: () => downloadFileWithProgress(url, name),
              onRemove: () => {
                if (isFile) {
                  setAttachments(
                    attachments.filter((item) => item.name !== name),
                  );
                  return;
                }

                onAttachmentListChange?.(
                  (attachmentList || []).filter((item) => item.url !== url),
                );
              },
            });
          }

          // if attachment is an already uploaded file, then show it as an attachment item
          if (!isFile) {
            return (
              <AttachmentItem
                key={url}
                className="list__item"
                attachment={item as IFileAttachment}
                onRemove={({ url }) => {
                  onAttachmentListChange?.(
                    (attachmentList || []).filter((item) => item.url !== url),
                  );
                }}
              />
            );
          }

          return (
            <div key={`file-${key}`} className="list__item">
              <span className="item__icon">
                {fileType ? (
                  getSpecificFileIcon(fileType)
                ) : (
                  <TMIcon
                    name={MEDIA_CONTENT.doc.icon}
                    size={20}
                    color="currentColor"
                  />
                )}
              </span>

              <div className="item__content">
                <Typography.Paragraph
                  className="item__content__title"
                  ellipsis={{ rows: 1, tooltip: name }}>
                  {name}
                </Typography.Paragraph>

                <Typography.Text
                  type="secondary"
                  className="item__content__subtitle">
                  {fileType ? fileType?.toUpperCase() : null}{' '}
                  {size
                    ? `- ${numFormatterInUnit(size, 0, 2, 'fileSize')}`
                    : null}
                </Typography.Text>
              </div>

              <div className="item__action">
                <Tooltip title="Remove" mouseEnterDelay={0.5}>
                  <TMIcon
                    className="item__action__btn"
                    name="trash-outline"
                    size={20}
                    color={COLORS.ERROR}
                    onClick={() => {
                      setAttachments(
                        attachments.filter((item: File) => item.name !== name),
                      );
                    }}
                  />
                </Tooltip>
              </div>
            </div>
          );
        })}

        {mediaItems.length >= maxFileCount ? null : (
          <Button
            className="siteBtn grayBtn outlineBtn"
            icon={<UploadOutlined />}
            disabled={mediaItems.length >= maxFileCount}
            onClick={() => {
              uploadFilesHandler({
                type: 'doc',
                mode: 'multiple',
                accept: allowedFileTypes.flatMap((type) =>
                  ACCEPT_FILE_TYPES[type] ? ACCEPT_FILE_TYPES[type] : [],
                ) as AcceptedFileType[],
                onUpload: (items, type) => {
                  uploadFiles({
                    files: items,
                    type,
                    maxSizeUnit: 'MB',
                    uploadToServer: false,
                    // convert bytes to MB
                    maxSize: maxFileSize / (1024 * 1024), // convert bytes to MB
                    uploadCallback: (list) => {
                      const newFiles = [...list];
                      const remainingFilesCount =
                        maxFileCount - mediaItems.length;

                      if (
                        newFiles.length > 0 &&
                        remainingFilesCount > 0 &&
                        newFiles.length > remainingFilesCount
                      ) {
                        message.warning(
                          `Maximum ${maxFileCount} attachments allowed. Only the first ${remainingFilesCount} will be uploaded.`,
                        );
                        uploadAttachments(
                          newFiles.slice(0, remainingFilesCount),
                        );
                        return;
                      }

                      uploadAttachments(newFiles.slice(0, maxFileCount));
                    },
                  });
                },
              });
            }}
            block>
            Click to upload
          </Button>
        )}
      </div>
    </div>
  );
};

export default memo(AddAttachments);
